/**
 * Question Management Component
 * 
 * Main component for managing question uploads. Handles CRUD operations,
 * filtering, and coordinates between list and upload modal views.
 * 
 * @component
 * @example
 * return <QuestionManagement />
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import CustomContainer from "../common/CustomContainer";
import QuestionList from "./QuestionList";
import UploadQuestionModal from "./UploadQuestionModal";
import StandardQuestionsFilterModal from "./StandardQuestionsFilterModal";
import QuestionService from "../../services/question-service";
import { getCustomerDetails } from "../../utils/apiClient";

/**
 * Question Management Main Component
 */
const QuestionManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [uploads, setUploads] = useState([]);
  const [standardQuestions, setStandardQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [selectedTab, setSelectedTab] = useState('custom'); // 'custom', 'standard', 'processing'
  const [searchQuery, setSearchQuery] = useState(''); // Immediate search input for UI
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Debounced search for API
  const [selectedPath, setSelectedPath] = useState(null); // For standard questions filter
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Cache for pagination data to avoid redundant API calls
  const cacheRef = useRef({});
  
  // Debounce timer for search
  const searchDebounceRef = useRef(null);
  
  // Request ID to track and prevent race conditions
  const requestIdRef = useRef(0);
  
  // Flag to track if initial mount has completed (to prevent duplicate API calls)
  const isInitialMountRef = useRef(true);

  /**
   * Read URL params
   */
  const readURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      tab: searchParams.get('tab') || 'custom',
      branch: searchParams.get('branch') || null,
      usecase: searchParams.get('usecase') || null,
      subusecase: searchParams.get('subusecase') || null
    };
  }, [location.search]);

  /**
   * Update URL params
   */
  const updateURLParams = useCallback((params) => {
    const searchParams = new URLSearchParams(location.search);
    
    Object.keys(params).forEach(key => {
      if (params[key]) {
        searchParams.set(key, params[key]);
      } else {
        searchParams.delete(key);
      }
    });
    
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  /**
   * Fetches customer details to get branch/usecase/subusecase options
   */
  const fetchCustomerDetails = useCallback(async () => {
    try {
      const response = await getCustomerDetails();
      if (response.statusCode === 'SUCCESS') {
        const details = response.response;
        
        // API returns useCasesRef, transform it to useCases for consistency
        if (details && details.useCasesRef) {
          // Transform flat useCasesRef array into grouped structure
          const groupedUseCases = [];
          const seen = new Set();
          
          details.useCasesRef.forEach(uc => {
            const key = `${uc.branch}-${uc.usecase}`;
            if (!seen.has(key)) {
              seen.add(key);
              const subUsecases = details.useCasesRef
                .filter(item => item.branch === uc.branch && item.usecase === uc.usecase)
                .map(item => item.subUsecase);
              
              groupedUseCases.push({
                branch: uc.branch,
                usecase: uc.usecase,
                subUsecases: subUsecases
              });
            }
          });
          
          setCustomerDetails({
            ...details,
            useCases: groupedUseCases
          });
        }
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      message.error('Failed to load customer configuration');
    }
  }, []);

  /**
   * Generates a cache key based on current filters
   * COMMENTED OUT: Cache key is now generated inline in fetchUploads to use the correct tab value
   */
  // const getCacheKey = useCallback(() => {
  //   const parts = [selectedTab, page, rowsPerPage, debouncedSearchQuery];
  //   if (selectedPath) {
  //     parts.push(selectedPath.branch, selectedPath.usecase, selectedPath.subusecase);
  //   }
  //   return parts.join('|');
  // }, [selectedTab, page, rowsPerPage, selectedPath, debouncedSearchQuery]);

  /**
   * Clears the pagination cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current = {};
  }, []);

  /**
   * Fetches question uploads based on selected tab and filters
   * Uses cache to avoid redundant API calls for previously loaded pages
   * When searching, fetches all data to enable frontend filtering across entire dataset
   * Uses requestIdRef to prevent race conditions when multiple requests are in flight
   */
  const fetchUploads = useCallback(async (tabOverride = null) => {
    // Use tabOverride if provided (for initial load), otherwise use selectedTab
    const currentTab = tabOverride || selectedTab;
    
    // Don't fetch uploads for standard questions tab
    if (currentTab === 'standard') {
      setLoading(false);
      return;
    }
    
    // Increment request ID to track this request
    const currentRequestId = ++requestIdRef.current;
    
    // Check cache first (with the correct tab)
    const cacheKeyParts = [currentTab, page, rowsPerPage, debouncedSearchQuery];
    if (selectedPath) {
      cacheKeyParts.push(selectedPath.branch, selectedPath.usecase, selectedPath.subusecase);
    }
    const cacheKey = cacheKeyParts.join('|');
    const cached = cacheRef.current[cacheKey];
    
    if (cached) {
      // Use cached data
      setUploads(cached.uploads);
      setTotalCount(cached.totalCount);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = {};
      
      // When searching, fetch all data for frontend filtering
      // Otherwise use pagination
      if (debouncedSearchQuery.trim()) {
        // Fetch all data for frontend search (no pagination)
        params.skip = 0;
        params.limit = 1000; // Large limit to get all data
      } else {
        // Normal pagination when not searching
        params.skip = page * rowsPerPage;
        params.limit = rowsPerPage;
      }
      
      // Add status filter based on current tab (using the correct tab value)
      if (currentTab === 'processing') {
        params.status = 'pending';
      } else if (currentTab === 'custom') {
        params.status = 'completed';
      }
      
      // Add path filters if selected
      if (selectedPath) {
        params.branch = selectedPath.branch;
        params.usecase = selectedPath.usecase;
        params.subusecase = selectedPath.subusecase;
      }
      
      const result = await QuestionService.getAll(params);
      
      // Check if this request is still the latest one (prevent race condition)
      if (currentRequestId !== requestIdRef.current) {
        // A newer request has been made, ignore this response
        console.log('Ignoring stale response for request:', currentRequestId, 'current:', requestIdRef.current);
        return;
      }
      
      if (result.success) {
        const uploads = result.uploads || [];
        const totalCount = result.totalCount || 0;
        
        // Cache the result
        cacheRef.current[cacheKey] = {
          uploads,
          totalCount,
          timestamp: Date.now()
        };
        
        setUploads(uploads);
        setTotalCount(totalCount);
      } else {
        message.error(result.message || 'Failed to fetch question uploads');
        setUploads([]);
        setTotalCount(0);
      }
    } catch (error) {
      // Check if this request is still the latest one before showing error
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      console.error('Error fetching uploads:', error);
      message.error('Failed to fetch question uploads');
      setUploads([]);
      setTotalCount(0);
    } finally {
      // Only set loading to false if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedTab, selectedPath, page, rowsPerPage, debouncedSearchQuery]);

  /**
   * Fetches standard questions for a specific path
   */
  const fetchStandardQuestions = useCallback(async (branch, usecase, subusecase) => {
    if (!branch || !usecase || !subusecase) {
      setStandardQuestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const result = await QuestionService.getStandardQuestionsForPath(branch, usecase, subusecase);
      
      if (result.success) {
        setStandardQuestions(result.questions || []);
      } else {
        message.error(result.message || 'Failed to fetch standard questions');
        setStandardQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching standard questions:', error);
      message.error('Failed to fetch standard questions');
      setStandardQuestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handles opening the upload modal
   */
  const handleOpenUploadModal = () => {
    if (!customerDetails) {
      message.warning('Loading customer configuration. Please try again in a moment.');
      return;
    }
    
    setUploadModalOpen(true);
  };

  /**
   * Handles closing the upload modal
   */
  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  /**
   * Handles successful upload
   */
  const handleUploadSuccess = async (redirectTab) => {
    handleCloseUploadModal();
    
    // Clear cache to show fresh data after upload
    clearCache();
    
    // Redirect to processing tab if specified
    if (redirectTab === 'processing') {
      setSelectedTab('processing');
      updateURLParams({ tab: 'processing' });
    }
    
    await fetchUploads();
  };

  /**
   * Handles tab change
   */
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    updateURLParams({ tab });
    
    // Clear search, cache and reset pagination when changing tabs
    setSearchQuery('');
    setDebouncedSearchQuery('');
    clearCache();
    setPage(0);
    
    // Auto-open filter modal if switching to standard tab with no path selected
    if (tab === 'standard' && !selectedPath) {
      setTimeout(() => setFilterModalOpen(true), 300);
    }
  };

  /**
   * Handles page change
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /**
   * Handles rows per page change
   */
  const handleRowsPerPageChange = (newRowsPerPage) => {
    clearCache(); // Clear cache when changing page size
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
  };

  /**
   * Handles search query change with debouncing
   */
  const handleSearchChange = (query) => {
    // Update search query immediately for UI responsiveness
    setSearchQuery(query);
    
    // Clear existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Debounce API call by 500ms
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchQuery(query);
      clearCache();
      setPage(0);
    }, 500);
  };
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  /**
   * Handles path selection for filters
   */
  const handlePathChange = useCallback((branch, usecase, subusecase) => {
    const newPath = { branch, usecase, subusecase };
    setSelectedPath(newPath);
    updateURLParams({ branch, usecase, subusecase });
    
    // Fetch appropriate data based on current tab
    if (selectedTab === 'standard') {
      fetchStandardQuestions(branch, usecase, subusecase);
    }
    // For custom/processing tabs, fetchUploads will be called automatically
    // via the useEffect that watches selectedPath
  }, [selectedTab, fetchStandardQuestions, updateURLParams]);

  /**
   * Handles opening the filter modal
   */
  const handleOpenFilterModal = () => {
    if (!customerDetails) {
      message.warning('Loading customer configuration. Please try again in a moment.');
      return;
    }
    setFilterModalOpen(true);
  };

  /**
   * Handles closing the filter modal
   */
  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  /**
   * Handles applying the filter from modal
   */
  const handleApplyFilter = ({ branch, usecase, subusecase }) => {
    // Clear cache when applying new filter
    clearCache();
    
    // If all values are null, clear the filter
    if (!branch && !usecase && !subusecase) {
      handleClearFilter();
    } else {
      handlePathChange(branch, usecase, subusecase);
    }
    setFilterModalOpen(false);
  };

  /**
   * Handles clearing the filter
   */
  const handleClearFilter = () => {
    setSelectedPath(null);
    updateURLParams({ branch: null, usecase: null, subusecase: null });
    
    // Clear cache and reset pagination when clearing filter
    clearCache();
    setPage(0);
    
    // Clear standard questions if on standard tab
    if (selectedTab === 'standard') {
      setStandardQuestions([]);
    }
    // For custom/processing tabs, fetchUploads will be called automatically
    // via the useEffect that watches selectedPath
  };

  // Initialize data on component mount and read URL params
  useEffect(() => {
    fetchCustomerDetails();
    
    // Read URL params
    const urlParams = readURLParams();
    
    // Set tab from URL
    setSelectedTab(urlParams.tab);
    
    // Set path from URL if available
    if (urlParams.branch && urlParams.usecase && urlParams.subusecase) {
      setSelectedPath({
        branch: urlParams.branch,
        usecase: urlParams.usecase,
        subusecase: urlParams.subusecase
      });
      
      // Fetch standard questions if on standard tab
      if (urlParams.tab === 'standard') {
        fetchStandardQuestions(urlParams.branch, urlParams.usecase, urlParams.subusecase);
      }
    } else if (urlParams.tab === 'standard') {
      // Auto-open filter modal if on standard tab with no selection
      setTimeout(() => setFilterModalOpen(true), 500);
    }
    
    // Fetch uploads for custom/processing tabs
    // Pass the tab from URL directly to avoid race condition with stale state
    if (urlParams.tab !== 'standard') {
      fetchUploads(urlParams.tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Fetch uploads when tab, filters, pagination, or search changes (except for standard tab)
  useEffect(() => {
    // Skip on initial mount since the mount useEffect handles the initial fetch
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    if (selectedTab !== 'standard') {
      fetchUploads();
    }
  }, [selectedTab, selectedPath, page, rowsPerPage, debouncedSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Frontend search filter for uploads
   * Filters by questionSetName, fileName, branch, usecase, subusecase
   */
  const filteredUploads = useMemo(() => {
    if (!searchQuery.trim()) return uploads;
    
    const query = searchQuery.toLowerCase().trim();
    
    return uploads.filter(upload => {
      const searchableFields = [
        upload.questionSetName,
        upload.fileName,
        upload.branch,
        upload.usecase,
        upload.subusecase,
        upload.status,
        upload.fileType,
        upload.metadata?.uploadedBy
      ];
      
      return searchableFields.some(field => 
        field && String(field).toLowerCase().includes(query)
      );
    });
  }, [uploads, searchQuery]);

  /**
   * Get the count of filtered results for display
   */
  const filteredCount = useMemo(() => {
    return filteredUploads.length;
  }, [filteredUploads]);

  /**
   * Apply frontend pagination to filtered results when searching
   */
  const paginatedUploads = useMemo(() => {
    if (!searchQuery.trim()) {
      // When not searching, backend handles pagination
      return filteredUploads;
    }
    
    // When searching, apply frontend pagination to filtered results
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredUploads.slice(startIndex, endIndex);
  }, [filteredUploads, searchQuery, page, rowsPerPage]);

  // Get counts for each tab (use filtered counts when searching)
  const getCounts = () => {
    const uploadsToCount = searchQuery.trim() ? filteredUploads : uploads;
    const customCount = uploadsToCount.filter(u => u.status === 'completed').length;
    const processingCount = uploadsToCount.filter(u => ['pending', 'processing'].includes(u.status)).length;
    
    return {
      custom: customCount,
      standard: standardQuestions.length,
      processing: processingCount
    };
  };

  const counts = getCounts();

  return (
    <CustomContainer containerWidth="90%">
      <QuestionList
        uploads={paginatedUploads}
        standardQuestions={standardQuestions}
        loading={loading}
        onOpenUploadModal={handleOpenUploadModal}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        counts={counts}
        onRefresh={fetchUploads}
        customerDetails={customerDetails}
        selectedPath={selectedPath}
        onPathChange={handlePathChange}
        onOpenFilterModal={handleOpenFilterModal}
        onClearFilter={handleClearFilter}
        // Pagination props
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={searchQuery.trim() ? filteredCount : totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      
      <UploadQuestionModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onSuccess={handleUploadSuccess}
        customerDetails={customerDetails}
        onPathChange={handlePathChange}
      />
      
      <StandardQuestionsFilterModal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        onApply={handleApplyFilter}
        customerDetails={customerDetails}
        initialValues={selectedPath}
        currentTab={selectedTab}
      />
    </CustomContainer>
  );
};

export default QuestionManagement;

